import { API_BASE_URL } from "../../config";
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/*
 * SCADA-style page for IceCys.
 *
 * Data source: /hwcontrol/api/v4/hardware  (token-authenticated hardware)
 *
 * Layout persistence:
 *   1. Tries backend GET/POST  /hwcontrol/api/v3/scada/layout
 *   2. Falls back to localStorage("scada_layout") if backend not available
 *      (so the page works immediately even before the backend route is added)
 *
 * Suggested backend contract (when you build it):
 *   GET  /hwcontrol/api/v3/scada/layout
 *        -> 200 { widgets: [...], updatedAt: "...", updatedBy: "..." }
 *        -> 404 if no layout has been saved yet
 *   POST /hwcontrol/api/v3/scada/layout
 *        body: { widgets: [...] }
 *        -> 200 OK
 *
 * Edit access: requires permission "scada:edit" on the user.
 */

const API_BASE = `${API_BASE_URL}/hwcontrol/api/v3`;
const HARDWARE_API_BASE = `${API_BASE_URL}/hwcontrol/api/v4/hardware`;
const LAYOUT_KEY = "scada_layout";
const HISTORY_LEN = 30;        // samples kept per chart widget
const POLL_MS = 5000;          // device data poll interval
const CANVAS_W = 1600;
const CANVAS_H = 900;
const UNGROUPED_TAG_GROUP = '__ungrouped_tag_group__';

// ------------------------------------------------------------------ helpers

const authHeaders = () => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const token = user?.token;
    return {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
    };
};

const hasScadaEdit = () => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const perms = user?.permissions || [];
    return perms.includes("superadmin:*") || perms.includes("scada:edit");
};

// Walk a value out of an object using dot-path ("foo.bar.baz" or "foo")
const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    const parts = path.split('.');
    let cur = obj;
    for (const p of parts) {
        if (cur == null) return undefined;
        cur = cur[p];
    }
    return cur;
};

// Safely parse a JSON string field; return raw if not JSON
const tryParse = (v) => {
    if (v == null) return v;
    if (typeof v !== 'string') return v;
    try { return JSON.parse(v); } catch (e) { return v; }
};

// Pull "lastDataJson" out of a device, parsed
const getDeviceLastData = (device) => {
    if (!device) return {};
    return tryParse(device.lastDataJson || device.lastData) || {};
};

// All available field paths across the device's lastData object (top-level only)
const listLastDataFields = (device) => {
    const d = getDeviceLastData(device);
    if (typeof d !== 'object' || d == null) return [];
    return Object.keys(d);
};

// Coerce a threshold input (which may be "", null, undefined, "5", 5, "  3 ")
// into a finite number, or null if "no limit set".
const parseThreshold = (t) => {
    if (t === "" || t == null) return null;
    const n = typeof t === 'number' ? t : Number(String(t).trim());
    return Number.isFinite(n) ? n : null;
};


// Inverts a numeric value relative to the widget's display range.
//   display = rangeMin + rangeMax - raw
// Use case: an ultrasonic sensor mounted at the top of a tank reports
// distance-to-water, but the operator wants to see the water level itself.
// Boolean / non-numeric values are passed through unchanged.
const applyInvert = (raw, widget) => {
    if (!widget || !widget.invert) return raw;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return raw;
    const min = Number(widget.rangeMin);
    const max = Number(widget.rangeMax);
    const lo = Number.isFinite(min) ? min : 0;
    const hi = Number.isFinite(max) ? max : 100;
    return lo + hi - n;
};

// Returns { alarm, why }.  Accepts already-resolved numeric thresholds plus
// optional label strings used in the "why" message.
const evaluateAlarm = (val, lo, hi, minLabel, maxLabel) => {
    const n = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(n)) return { alarm: false, why: '' };
    const ml = minLabel || 'min';
    const xl = maxLabel || 'max';
    if (lo !== null && lo !== undefined && n < lo) return { alarm: true, why: `${n} < ${ml} ${lo}` };
    if (hi !== null && hi !== undefined && n > hi) return { alarm: true, why: `${n} > ${xl} ${hi}` };
    return { alarm: false, why: '' };
};

// Resolves the alarm thresholds for a widget against a device's lastData.
// If alertMinField is set, the live value of that field is used; otherwise the
// constant alertMin is used.  Same for max.  Returns:
//   { lo, hi, minLabel, maxLabel }
//
// Invert is a purely visual transform — it does NOT change the units of
// thresholds.  Both constants and field-referenced thresholds stay in raw units
// (matching what the device reports). Alarm comparison happens against the raw
// value in DraggableWidget.
const resolveThresholds = (widget, lastData) => {
    let lo = null, hi = null, minLabel = 'min', maxLabel = 'max';
    if (widget.alertMinField) {
        lo = parseThreshold(getByPath(lastData, widget.alertMinField));
        minLabel = `min(${widget.alertMinField})`;
    } else {
        lo = parseThreshold(widget.alertMin);
    }
    if (widget.alertMaxField) {
        hi = parseThreshold(getByPath(lastData, widget.alertMaxField));
        maxLabel = `max(${widget.alertMaxField})`;
    } else {
        hi = parseThreshold(widget.alertMax);
    }
    return { lo, hi, minLabel, maxLabel };
};

// Convenience: resolve + evaluate in one call.
const resolveAndEvaluate = (val, widget, lastData) => {
    const r = resolveThresholds(widget, lastData);
    return evaluateAlarm(val, r.lo, r.hi, r.minLabel, r.maxLabel);
};

// Kept for callers that only need the boolean.
const isOutOfRange = (val, min, max) =>
    evaluateAlarm(val, parseThreshold(min), parseThreshold(max)).alarm;

const sizeFor = (type) => {
    if (type === 'chart') return { w: 260, h: 140 };
    if (type === 'gauge') return { w: 180, h: 160 };
    if (type === 'tank') return { w: 100, h: 200 };
    if (type === 'pump') return { w: 100, h: 100 };
    if (type === 'lamp') return { w: 100, h: 100 };
    if (type === 'toggle') return { w: 140, h: 60 };
    if (type === 'label') return { w: 200, h: 50 };
    if (type === 'line') return { w: 0, h: 0 }; // line uses x1/y1/x2/y2
    return { w: 180, h: 90 };
};

const labelFor = (type) => {
    if (type === 'status') return 'Status';
    if (type === 'lamp')   return 'Lamp';
    if (type === 'toggle') return 'Toggle';
    if (type === 'tank')   return 'Tank';
    if (type === 'pump')   return 'Pump';
    if (type === 'label')  return 'Title text';
    if (type === 'line')   return '';
    return 'Value';
};

const newWidget = (type) => {
    const { w, h } = sizeFor(type);
    const base = {
        id: `w_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        type,
        x: 60, y: 60, w, h,
        label: labelFor(type),
        hwid: '',
        field: '',
        unit: '',
        rangeMin: 0,
        rangeMax: 100,
        alertMin: '', alertMax: '',
        alertMinField: '', alertMaxField: '',
        invert: false,                 // display = rangeMin + rangeMax - raw  (e.g. sonar over tank)
    };
    if (type === 'toggle') {
        return { ...base,
            onValue: '1',         // value of `field` that means ON
            onCommand: '{"action":"on"}',
            offCommand: '{"action":"off"}',
            commandRef: ''
        };
    }
    if (type === 'label') {
        return { ...base, fontSize: 18, color: '#222', bold: true };
    }
    if (type === 'line') {
        return { ...base,
            mode: 'free',          // 'free' | 'anchored'
            x1: 100, y1: 100, x2: 300, y2: 100,
            anchorStart: '', anchorEnd: '',
            color: '#444', strokeWidth: 3,
            dashed: false, arrow: 'none',  // 'none' | 'end' | 'both'
            colorField: '', onColor: '#46af38', offColor: '#999',
        };
    }
    return base;
};

// ------------------------------------------------------------------ root

const ScadaPage = () => {
    const [devices, setDevices] = useState([]);
    const [tagGroupByHwid, setTagGroupByHwid] = useState({});
    const [error, setError] = useState(null);
    const [loadingLayout, setLoadingLayout] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [widgets, setWidgets] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [savedSource, setSavedSource] = useState('');   // 'backend' | 'local' | ''
    const [saveStatus, setSaveStatus] = useState('');
    const canEdit = hasScadaEdit();

    // Per-widget rolling history for chart type, keyed by widget id
    const historyRef = useRef({});

    // ----------------------- load devices (poll)
    const fetchDevices = useCallback(async () => {
        try {
            const url = HARDWARE_API_BASE;
            const headers = authHeaders();
            const resp = await fetch(url, { method: 'GET', headers });
            if (resp.ok) {
                const data = await resp.json();
                setDevices(data);
                const tokenResp = await fetch(`${API_BASE}/hardware/tokens`, {
                    method: 'GET', headers
                }).catch(() => null);
                if (tokenResp?.ok) {
                    const tokens = await tokenResp.json();
                    const latestGroupByHwid = {};
                    [...tokens]
                        .filter(tokenItem => !tokenItem.revoked && tokenItem.tagGroupName)
                        .sort((a, b) => b.id - a.id)
                        .forEach(tokenItem => {
                            if (!latestGroupByHwid[tokenItem.hwid]) {
                                latestGroupByHwid[tokenItem.hwid] = tokenItem.tagGroupName;
                            }
                        });
                    setTagGroupByHwid(latestGroupByHwid);
                } else {
                    setTagGroupByHwid({});
                }
                setError(null);
            } else {
                setError(`Failed to fetch hardware: ${resp.status}`);
            }
        } catch (e) {
            setError(`Error fetching hardware: ${e.message}`);
        }
    }, []);

    useEffect(() => {
        fetchDevices();
        const t = setInterval(fetchDevices, POLL_MS);
        return () => clearInterval(t);
    }, [fetchDevices]);

    // ----------------------- load layout (backend, then localStorage)
    useEffect(() => {
        const loadLayout = async () => {
            setLoadingLayout(true);
            try {
            const resp = await fetch(`${API_BASE}/scada/layout`, {
                method: 'GET', headers: authHeaders()
            });
            if (resp.ok) {
                const data = await resp.json();
                if (Array.isArray(data?.widgets)) {
                    console.log('📥 Layout response (GET from backend):', JSON.stringify({ 
                        ok: resp.ok, 
                        status: resp.status, 
                        widgetCount: data.widgets.length 
                    }, null, 2));
                    setWidgets(data.widgets);
                    setSavedSource('backend');
                    setLoadingLayout(false);
                    return;
                }
            }
        } catch (e) { /* fall through to localStorage */ }

            const local = localStorage.getItem(LAYOUT_KEY);
            if (local) {
                try {
                    const parsed = JSON.parse(local);
                    if (Array.isArray(parsed?.widgets)) {
                        setWidgets(parsed.widgets);
                        setSavedSource('local');
                    }
                } catch (e) { /* ignore */ }
            }
            setLoadingLayout(false);
        };
        loadLayout();
    }, []);

    // ----------------------- record chart history every poll
    useEffect(() => {
        widgets.forEach(w => {
            if (w.type !== 'chart') return;
            const dev = devices.find(d => d.hwid === w.hwid);
            const raw = getByPath(getDeviceLastData(dev), w.field);
            const v = Number(applyInvert(raw, w));
            if (Number.isNaN(v)) return;
            const arr = historyRef.current[w.id] || [];
            arr.push({ t: Date.now(), v });
            while (arr.length > HISTORY_LEN) arr.shift();
            historyRef.current[w.id] = arr;
        });
    }, [devices, widgets]);

    // ----------------------- save
    const saveLayout = async () => {
        const payload = { widgets };
        setSaveStatus('Saving...');
        try {
            const resp = await fetch(`${API_BASE}/scada/layout`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify(payload)
            });
            console.log('🔹 SCADA API Call - POST Layout:', JSON.stringify({
                url: `${API_BASE}/scada/layout`,
                method: 'POST',
                headers: authHeaders(),
                body: payload
            }, null, 2));
            
            if (resp.ok) {
                console.log('✅ Layout saved (POST):', JSON.stringify({ 
                    status: resp.status,
                    widgetCount: payload.widgets.length 
                }, null, 2));
                setSaveStatus('Saved to server');
                setSavedSource('backend');
                // also keep a local copy as backup
                localStorage.setItem(LAYOUT_KEY, JSON.stringify(payload));
                setTimeout(() => setSaveStatus(''), 2500);
                return;
            }
            console.log('⚠️ Backend not available:', JSON.stringify({ 
                status: resp.status,
                reason: 'saved locally' 
            }, null, 2));
            // Backend not available → save locally
            localStorage.setItem(LAYOUT_KEY, JSON.stringify(payload));
            setSavedSource('local');
            setSaveStatus(`Backend unavailable (${resp.status}) — saved locally`);
            setTimeout(() => setSaveStatus(''), 4000);
        } catch (e) {
            console.log('❌ Backend error:', JSON.stringify({ message: e.message }));
            localStorage.setItem(LAYOUT_KEY, JSON.stringify(payload));
            setSavedSource('local');
            setSaveStatus(`Backend error — saved locally`);
            setTimeout(() => setSaveStatus(''), 4000);
        }
    };

    // ----------------------- widget mutations
    const addWidget = (type) => {
        const w = newWidget(type);
        if (devices.length > 0) w.hwid = devices[0].hwid;
        setWidgets(prev => [...prev, w]);
        setSelectedId(w.id);
    };
    const updateWidget = (id, patch) => {
        setWidgets(prev => prev.map(w => w.id === id ? { ...w, ...patch } : w));
    };
    const deleteWidget = (id) => {
        setWidgets(prev => prev.filter(w => w.id !== id));
        if (selectedId === id) setSelectedId(null);
        delete historyRef.current[id];
    };

    const selectedWidget = useMemo(
        () => widgets.find(w => w.id === selectedId) || null,
        [widgets, selectedId]
    );

    const deviceGroups = useMemo(() => {
        const groups = devices.reduce((result, device) => {
            const name = tagGroupByHwid[device.hwid] || UNGROUPED_TAG_GROUP;
            if (!result[name]) result[name] = [];
            result[name].push(device);
            return result;
        }, {});
        return Object.entries(groups)
            .map(([name, groupDevices]) => ({ name, devices: groupDevices }))
            .sort((left, right) => {
                if (left.name === UNGROUPED_TAG_GROUP) return 1;
                if (right.name === UNGROUPED_TAG_GROUP) return -1;
                return left.name.localeCompare(right.name, 'th');
            });
    }, [devices, tagGroupByHwid]);

    // ----------------------- render
    return (
        <div style={{ padding: '15px', fontFamily: 'sans-serif' }}>
            <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px'
            }}>
                <div>
                    <h2 style={{ margin: 0 }}>SCADA View</h2>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                        {loadingLayout
                            ? 'Loading layout…'
                            : `Layout source: ${savedSource || 'none yet'} • ${widgets.length} widget(s)`}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {saveStatus && (
                        <span style={{ fontSize: '0.85em', color: '#0066cc' }}>{saveStatus}</span>
                    )}
                    {error && (
                        <span style={{ fontSize: '0.85em', color: 'red' }}>{error}</span>
                    )}
                    {canEdit && (
                        <>
                            <button
                                onClick={() => { setEditMode(e => !e); setSelectedId(null); }}
                                style={{
                                    padding: '6px 14px',
                                    backgroundColor: editMode ? '#dc3545' : '#28a745',
                                    color: 'white', border: 'none', borderRadius: '4px',
                                    cursor: 'pointer', fontWeight: 'bold'
                                }}>
                                {editMode ? 'Exit Edit' : 'Edit Dashboard'}
                            </button>
                            {editMode && (
                                <button
                                    onClick={saveLayout}
                                    style={{
                                        padding: '6px 14px', backgroundColor: '#007bff',
                                        color: 'white', border: 'none', borderRadius: '4px',
                                        cursor: 'pointer', fontWeight: 'bold'
                                    }}>
                                    Save
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                {/* Toolbox */}
                {editMode && (
                    <div style={{
                        width: '230px', minWidth: '230px',
                        border: '1px solid #ddd', borderRadius: '4px',
                        padding: '10px', backgroundColor: '#fafafa',
                        height: 'fit-content', position: 'sticky', top: '10px'
                    }}>
                        <strong style={{ display: 'block', marginBottom: '8px' }}>Add widget</strong>
                        {[
                            { t: 'numeric', label: 'Numeric value' },
                            { t: 'status',  label: 'Status dot' },
                            { t: 'lamp',    label: 'Lamp (on/off)' },
                            { t: 'toggle',  label: 'Toggle (sends cmd)' },
                            { t: 'gauge',   label: 'Gauge' },
                            { t: 'tank',    label: 'Tank (fill bar)' },
                            { t: 'pump',    label: 'Pump (rotating)' },
                            { t: 'chart',   label: 'Chart (sparkline)' },
                            { t: 'label',   label: 'Label / title' },
                            { t: 'line',    label: 'Connection line' }
                        ].map(({ t, label }) => (
                            <button key={t} onClick={() => addWidget(t)}
                                style={{
                                    display: 'block', width: '100%', marginBottom: '5px',
                                    padding: '6px', cursor: 'pointer', textAlign: 'left',
                                    border: '1px solid #ccc', borderRadius: '3px',
                                    backgroundColor: 'white',
                                    color: '#222', fontWeight: 'normal', fontSize: '0.9em'
                                }}>
                                + {label}
                            </button>
                        ))}

                        {/* All widgets list — handy for finding off-screen / hidden widgets */}
                        <div style={{ marginTop: '12px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                            <strong style={{ display: 'block', marginBottom: '6px', color: '#222', fontSize: '0.9em' }}>
                                All widgets ({widgets.length})
                            </strong>
                            <div style={{
                                maxHeight: '220px', overflowY: 'auto',
                                border: '1px solid #eee', borderRadius: '3px',
                                backgroundColor: 'white'
                            }}>
                                {widgets.length === 0 && (
                                    <div style={{ padding: '8px', fontSize: '0.8em', color: '#888' }}>
                                        No widgets yet
                                    </div>
                                )}
                                {widgets.map(w => {
                                    const isSelected = selectedId === w.id;
                                    const display = w.label && w.label.trim() ? w.label : '(no label)';
                                    const handleSelect = () => {
                                        setSelectedId(w.id);
                                        // scroll the canvas viewport so the widget is visible
                                        const inner = document.getElementById('scada-canvas-inner');
                                        const viewport = inner && inner.parentElement;
                                        if (viewport && w.type !== 'line') {
                                            viewport.scrollTo({
                                                left: Math.max(0, (w.x || 0) - 60),
                                                top: Math.max(0, (w.y || 0) - 60),
                                                behavior: 'smooth'
                                            });
                                        } else if (viewport && w.type === 'line') {
                                            const x = w.mode === 'free' ? Number(w.x1) : 0;
                                            const y = w.mode === 'free' ? Number(w.y1) : 0;
                                            viewport.scrollTo({
                                                left: Math.max(0, x - 60),
                                                top: Math.max(0, y - 60),
                                                behavior: 'smooth'
                                            });
                                        }
                                    };
                                    return (
                                        <div key={w.id}
                                            onClick={handleSelect}
                                            style={{
                                                display: 'flex', alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '4px 6px',
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                                borderBottom: '1px solid #f5f5f5',
                                                fontSize: '0.78em'
                                            }}>
                                            <span style={{
                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap', flex: 1
                                            }}>
                                                <span style={{
                                                    color: '#888', display: 'inline-block',
                                                    minWidth: '52px'
                                                }}>{w.type}</span>
                                                <span style={{ color: '#222' }}>{display}</span>
                                            </span>
                                            <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Delete widget "' + display + '"?')) {
                                                        deleteWidget(w.id);
                                                    }
                                                }}
                                                title="Delete widget"
                                                style={{
                                                    backgroundColor: 'transparent', color: '#dc3545',
                                                    border: 'none', cursor: 'pointer', fontWeight: 'bold',
                                                    padding: '0 6px', fontSize: '1em', lineHeight: 1
                                                }}>
                                                X
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedWidget && (
                            <WidgetConfig
                                widget={selectedWidget}
                                devices={devices}
                                deviceGroups={deviceGroups}
                                widgets={widgets}
                                onChange={(patch) => updateWidget(selectedWidget.id, patch)}
                                onDelete={() => deleteWidget(selectedWidget.id)}
                            />
                        )}
                    </div>
                )}

                {/* Canvas */}
                <div style={{
                    flex: 1, position: 'relative',
                    width: '100%', minHeight: `${CANVAS_H}px`,
                    border: editMode ? '2px dashed #999' : '1px solid #ddd',
                    backgroundColor: editMode ? '#f4f4f4' : '#fff',
                    backgroundImage: editMode
                        ? 'linear-gradient(#e0e0e0 1px, transparent 1px),' +
                          'linear-gradient(90deg, #e0e0e0 1px, transparent 1px)'
                        : 'none',
                    backgroundSize: '20px 20px',
                    overflow: 'auto'
                }}
                    onClick={() => editMode && setSelectedId(null)}>
                    <div id="scada-canvas-inner" data-scada-canvas style={{ position: 'relative', width: `${CANVAS_W}px`, height: `${CANVAS_H}px` }}>
                        {/* SVG layer for connection lines, behind the box widgets */}
                        <svg width={CANVAS_W} height={CANVAS_H}
                            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                            {widgets.filter(w => w.type === 'line').map(line => (
                                <ConnectionLine key={line.id} line={line}
                                    widgets={widgets} devices={devices}
                                    editMode={editMode}
                                    selected={selectedId === line.id}
                                    onSelect={() => setSelectedId(line.id)} />
                            ))}
                        </svg>
                        {widgets.filter(w => w.type !== 'line').map(w => (
                            <DraggableWidget
                                key={w.id}
                                widget={w}
                                devices={devices}
                                editMode={editMode}
                                selected={selectedId === w.id}
                                history={historyRef.current[w.id] || []}
                                onSelect={() => setSelectedId(w.id)}
                                onMove={(x, y) => updateWidget(w.id, { x, y })}
                                onResize={(width, height) => updateWidget(w.id, { w: width, h: height })}
                            />
                        ))}
                        {widgets.length === 0 && !loadingLayout && (
                            <div style={{
                                position: 'absolute', top: '50%', left: '50%',
                                transform: 'translate(-50%, -50%)', color: '#888',
                                fontSize: '1.1em'
                            }}>
                                {canEdit
                                    ? 'No widgets yet. Click "Edit Dashboard" then add some.'
                                    : 'No widgets configured. Ask an admin with "scada:edit" permission to set up the dashboard.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ------------------------------------------------------------------ Draggable

const DraggableWidget = ({ widget, devices, editMode, selected, history, onSelect, onMove, onResize }) => {
    const [drag, setDrag] = useState(null);   // { dx, dy }
    const [resz, setResz] = useState(null);   // { startW, startH, sx, sy }

    const onMouseDown = (e) => {
        if (!editMode) return;
        e.stopPropagation();
        onSelect();
        const rect = e.currentTarget.getBoundingClientRect();
        setDrag({ dx: e.clientX - rect.left, dy: e.clientY - rect.top });
    };

    const onResizeMouseDown = (e) => {
        if (!editMode) return;
        e.stopPropagation();
        e.preventDefault();
        setResz({ startW: widget.w, startH: widget.h, sx: e.clientX, sy: e.clientY });
    };

    useEffect(() => {
        if (!drag && !resz) return;
        const onMove2 = (e) => {
            if (drag) {
                // find canvas-relative position
                const canvas = document.getElementById('scada-canvas-inner') ||
                    document.querySelector('[data-scada-canvas]');
                let cx = 0, cy = 0;
                if (canvas) {
                    const r = canvas.getBoundingClientRect();
                    cx = r.left; cy = r.top;
                }
                const x = Math.max(0, e.clientX - cx - drag.dx);
                const y = Math.max(0, e.clientY - cy - drag.dy);
                onMove(Math.round(x / 5) * 5, Math.round(y / 5) * 5);
            } else if (resz) {
                const newW = Math.max(80, resz.startW + (e.clientX - resz.sx));
                const newH = Math.max(50, resz.startH + (e.clientY - resz.sy));
                onResize(Math.round(newW / 5) * 5, Math.round(newH / 5) * 5);
            }
        };
        const onUp = () => { setDrag(null); setResz(null); };
        window.addEventListener('mousemove', onMove2);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove2);
            window.removeEventListener('mouseup', onUp);
        };
    }, [drag, resz, onMove, onResize]);

    const device = devices.find(d => d.hwid === widget.hwid);
    const lastData = getDeviceLastData(device);
    const rawValue = getByPath(lastData, widget.field);
    const value = applyInvert(rawValue, widget); // used for display only
    // Alarm comparison uses the RAW value against the RAW thresholds, so invert is
    // purely a visual transform. User types min/max in the same units the device
    // reports in.
    const { alarm, why } = resolveAndEvaluate(rawValue, widget, lastData);

    return (
        <div
            data-scada-widget
            onMouseDown={onMouseDown}
            onClick={(e) => { e.stopPropagation(); if (editMode) onSelect(); }}
            style={{
                position: 'absolute',
                left: `${widget.x}px`, top: `${widget.y}px`,
                width: `${widget.w}px`, height: `${widget.h}px`,
                border: alarm
                    ? '2px solid #dc3545'
                    : selected ? '2px solid #007bff' : '1px solid #999',
                backgroundColor: alarm ? '#fff5f5' : 'white',
                borderRadius: '4px',
                boxShadow: alarm
                    ? '0 0 8px rgba(220,53,69,0.6)'
                    : '0 1px 3px rgba(0,0,0,0.1)',
                cursor: editMode ? 'move' : 'default',
                userSelect: 'none',
                overflow: 'hidden',
                animation: alarm ? 'scada-pulse 1s infinite' : 'none'
            }}>
            <WidgetBody widget={widget} value={value} alarm={alarm} history={history} />
            {alarm && (
                <div title={'ALARM: ' + why}
                    style={{
                        position: 'absolute', top: 3, right: 3,
                        width: '16px', height: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: '#dc3545', color: 'white',
                        fontSize: '0.7em', fontWeight: 'bold',
                        lineHeight: 1,
                        pointerEvents: 'none'
                    }}>
                    !
                </div>
            )}
            {editMode && (
                <div
                    onMouseDown={onResizeMouseDown}
                    style={{
                        position: 'absolute', right: 0, bottom: 0,
                        width: '14px', height: '14px',
                        backgroundColor: '#007bff', cursor: 'nwse-resize',
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
                    }}
                />
            )}
        </div>
    );
};

// ------------------------------------------------------------------ Widget bodies

const WidgetBody = ({ widget, value, alarm, history }) => {
    if (widget.type === 'numeric') return <NumericBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'status') return <StatusBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'lamp')    return <LampBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'toggle')  return <ToggleBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'gauge')  return <GaugeBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'tank')   return <TankBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'pump')   return <PumpBody widget={widget} value={value} alarm={alarm} />;
    if (widget.type === 'chart')  return <ChartBody widget={widget} value={value} alarm={alarm} history={history} />;
    if (widget.type === 'label')  return <LabelBody widget={widget} />;
    return null;
};

const NumericBody = ({ widget, value, alarm }) => (
    <div style={{
        padding: '8px', height: '100%', display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between'
    }}>
        <div style={{ fontSize: '0.85em', color: '#666' }}>{widget.label}</div>
        <div style={{
            fontSize: '1.7em', fontWeight: 'bold',
            color: alarm ? '#dc3545' : '#222', textAlign: 'center'
        }}>
            {value == null || value === '' ? '—' : `${value}${widget.unit ? ' ' + widget.unit : ''}`}
        </div>
        <div style={{ fontSize: '0.7em', color: '#999', textAlign: 'right' }}>
            {widget.hwid || 'no device'} / {widget.field || 'no field'}
        </div>
    </div>
);

const StatusBody = ({ widget, value, alarm }) => {
    // Status is "good" when value is truthy AND not in alarm
    const truthy = value === true || value === 'true' || value === 1 || value === '1' || (typeof value === 'string' && value.toLowerCase() === 'on');
    const color = alarm ? '#dc3545' : (truthy ? '#28a745' : '#6c757d');
    return (
        <div style={{
            padding: '8px', height: '100%',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center'
        }}>
            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>{widget.label}</div>
            <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                backgroundColor: color, boxShadow: `0 0 10px ${color}`
            }} />
            <div style={{ fontSize: '0.85em', marginTop: '6px', color: '#444' }}>
                {value == null ? '—' : String(value)}
            </div>
        </div>
    );
};

const GaugeBody = ({ widget, value, alarm }) => {
    const min = Number(widget.rangeMin) || 0;
    const max = Number(widget.rangeMax) || 100;
    const num = Number(value);
    const pct = Number.isNaN(num) ? 0 :
        Math.max(0, Math.min(1, (num - min) / (max - min || 1)));
    // arc 180deg from -90 (left) to 90 (right)
    const angle = -90 + pct * 180;
    const cx = widget.w / 2, cy = widget.h - 16, r = Math.min(widget.w, widget.h * 1.6) / 2 - 10;

    const polar = (deg, rad) => {
        const a = (deg - 90) * Math.PI / 180;
        return [cx + rad * Math.cos(a), cy + rad * Math.sin(a)];
    };

    // Single semicircle path used for both the track and the fill.
    // The fill uses stroke-dasharray to draw only the first `pct * arcLength`
    // pixels of the path, so the direction is always identical to the track
    // (no SVG sweep/large-flag confusion).  Arc length of a semicircle = pi*r.
    // sweep-flag = 1 here makes the path go CLOCKWISE (in user coords) from the
    // left endpoint to the right endpoint, which in this widget's geometry traces
    // the TOP half of the imagined circle — the visible gauge shape.
    const arcPath = `M ${polar(-90, r)[0]} ${polar(-90, r)[1]} A ${r} ${r} 0 0 1 ${polar(90, r)[0]} ${polar(90, r)[1]}`;
    const arcLength = Math.PI * r;
    const filledLength = pct * arcLength;

    return (
        <div style={{ padding: '4px', height: '100%', position: 'relative' }}>
            <div style={{ fontSize: '0.8em', color: '#666' }}>{widget.label}</div>
            <svg width="100%" height={widget.h - 28} viewBox={`0 0 ${widget.w} ${widget.h}`}>
                <path d={arcPath} stroke="#ddd" strokeWidth="10" fill="none" strokeLinecap="round" />
                <path d={arcPath} stroke={alarm ? '#dc3545' : '#007bff'} strokeWidth="10"
                    fill="none" strokeLinecap="round"
                    strokeDasharray={`${filledLength} ${arcLength}`} />
                <text x={cx} y={cy - 4} textAnchor="middle" fontSize="18" fontWeight="bold"
                    fill={alarm ? '#dc3545' : '#222'}>
                    {Number.isNaN(num) ? '—' : num.toFixed(1)}
                </text>
                <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="#888">
                    {widget.unit}
                </text>
            </svg>
        </div>
    );
};

const ChartBody = ({ widget, value, alarm, history }) => {
    const W = widget.w - 10, H = widget.h - 28;
    const min = Number(widget.rangeMin);
    const max = Number(widget.rangeMax);
    const ymin = Number.isNaN(min) ? 0 : min;
    const ymax = Number.isNaN(max) || max <= ymin ? ymin + 1 : max;
    const points = history.length === 0
        ? ''
        : history.map((p, i) => {
            const x = (i / Math.max(1, HISTORY_LEN - 1)) * W;
            const yPct = (p.v - ymin) / (ymax - ymin);
            const y = H - Math.max(0, Math.min(1, yPct)) * H;
            return `${x},${y}`;
        }).join(' ');
    const last = history[history.length - 1];

    return (
        <div style={{ padding: '4px', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
                <span style={{ color: '#666' }}>{widget.label}</span>
                <span style={{ fontWeight: 'bold', color: alarm ? '#dc3545' : '#222' }}>
                    {last == null ? '—' : `${last.v.toFixed(2)} ${widget.unit || ''}`}
                </span>
            </div>
            <svg width={W} height={H} style={{ display: 'block' }}>
                <line x1="0" y1="0" x2={W} y2="0" stroke="#eee" />
                <line x1="0" y1={H} x2={W} y2={H} stroke="#eee" />
                {(() => {
                    const c = parseThreshold(widget.alertMax);
                    if (c === null || widget.alertMaxField) return null;
                    const y = H - ((c - ymin) / (ymax - ymin)) * H;
                    return <line x1="0" y1={y} x2={W} y2={y} stroke="#dc3545" strokeDasharray="3,3" />;
                })()}
                {(() => {
                    const c = parseThreshold(widget.alertMin);
                    if (c === null || widget.alertMinField) return null;
                    const y = H - ((c - ymin) / (ymax - ymin)) * H;
                    return <line x1="0" y1={y} x2={W} y2={y} stroke="#dc3545" strokeDasharray="3,3" />;
                })()}
                {points && (
                    <polyline points={points} fill="none"
                        stroke={alarm ? '#dc3545' : '#007bff'} strokeWidth="2" />
                )}
            </svg>
        </div>
    );
};



// ============================================================ FACTORY WIDGETS

const isTruthyValue = (v, onValue) => {
    if (onValue !== undefined && onValue !== '') {
        return String(v) === String(onValue);
    }
    return v === true || v === 'true' || v === 1 || v === '1' ||
        (typeof v === 'string' && v.toLowerCase() === 'on');
};

const LampBody = ({ widget, value, alarm }) => {
    const on = isTruthyValue(value, widget.onValue);
    const color = alarm ? '#dc3545' : (on ? '#28a745' : '#6c757d');
    return (
        <div style={{
            padding: '6px', height: '100%',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center'
        }}>
            <div style={{ fontSize: '0.8em', color: '#444', marginBottom: '4px' }}>{widget.label}</div>
            <div style={{
                flex: 1, width: '100%',
                borderRadius: '8px',
                background: `radial-gradient(circle at 30% 30%, ${color}cc, ${color})`,
                boxShadow: `inset 0 0 10px rgba(0,0,0,0.3), 0 0 12px ${color}66`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 'bold', fontSize: '0.9em'
            }}>
                {on ? 'ON' : 'OFF'}
            </div>
        </div>
    );
};

const ToggleBody = ({ widget, value, alarm }) => {
    const on = isTruthyValue(value, widget.onValue);
    const [busy, setBusy] = useState(false);
    const send = async (cmdText) => {
        if (!widget.hwid) { alert('Toggle widget has no hwid set'); return; }
        let cmd;
        try { cmd = JSON.parse(cmdText); } catch (e) { cmd = cmdText; }
        setBusy(true);
        try {
            const body = { command: cmd };
            if (widget.commandRef) body.commandRef = widget.commandRef;
            const resp = await fetch(`${HARDWARE_API_BASE}/${widget.hwid}/command`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify(body)
            });
            
            if (!resp.ok) alert(`Toggle failed: ${resp.status}`);
        } catch (e) { 
            alert('Toggle error: ' + e.message); 
        }
        finally { setBusy(false); }
    };
    const handleClick = (e) => {
        e.stopPropagation();
        if (busy) return;
        send(on ? widget.offCommand : widget.onCommand);
    };
    return (
        <div style={{
            padding: '4px', height: '100%',
            display: 'flex', flexDirection: 'column'
        }}>
            <div style={{ fontSize: '0.75em', color: '#444' }}>{widget.label}</div>
            <button onClick={handleClick} disabled={busy}
                style={{
                    flex: 1, marginTop: '2px',
                    backgroundColor: alarm ? '#dc3545' : (on ? '#28a745' : '#6c757d'),
                    color: 'white', border: 'none', borderRadius: '4px',
                    cursor: busy ? 'wait' : 'pointer',
                    fontWeight: 'bold', fontSize: '1em'
                }}>
                {busy ? '...' : (on ? 'ON  (click OFF)' : 'OFF  (click ON)')}
            </button>
        </div>
    );
};

const TankBody = ({ widget, value, alarm }) => {
    const min = Number(widget.rangeMin) || 0;
    const max = Number(widget.rangeMax) || 100;
    const num = Number(value);
    const pct = Number.isFinite(num)
        ? Math.max(0, Math.min(1, (num - min) / (max - min || 1)))
        : 0;
    const fillH = `${pct * 100}%`;
    const fillColor = alarm ? '#dc3545' : '#1e88e5';
    return (
        <div style={{
            padding: '4px', height: '100%',
            display: 'flex', flexDirection: 'column'
        }}>
            <div style={{ fontSize: '0.75em', color: '#444', textAlign: 'center' }}>{widget.label}</div>
            <div style={{
                flex: 1, position: 'relative',
                border: '2px solid #555', borderRadius: '4px',
                backgroundColor: '#f0f0f0', overflow: 'hidden',
                margin: '4px 8px'
            }}>
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: fillH, backgroundColor: fillColor,
                    transition: 'height 0.5s ease'
                }} />
                <div style={{
                    position: 'absolute', top: '50%', left: 0, right: 0,
                    transform: 'translateY(-50%)',
                    textAlign: 'center', fontWeight: 'bold',
                    color: pct > 0.5 ? 'white' : '#222',
                    fontSize: '1em', textShadow: '0 0 2px rgba(0,0,0,0.3)'
                }}>
                    {Number.isFinite(num) ? num.toFixed(1) : '-'}{widget.unit ? ' ' + widget.unit : ''}
                </div>
            </div>
        </div>
    );
};

const PumpBody = ({ widget, value, alarm }) => {
    const on = isTruthyValue(value, widget.onValue);
    const color = alarm ? '#dc3545' : (on ? '#28a745' : '#6c757d');
    return (
        <div style={{
            padding: '4px', height: '100%',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center'
        }}>
            <div style={{ fontSize: '0.75em', color: '#444' }}>{widget.label}</div>
            <svg viewBox="0 0 100 100" width="70%" height="70%">
                <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="6" />
                <g style={on ? { transformOrigin: '50px 50px', animation: 'scada-spin 1.2s linear infinite' } : {}}>
                    <line x1="50" y1="15" x2="50" y2="85" stroke={color} strokeWidth="6" />
                    <line x1="15" y1="50" x2="85" y2="50" stroke={color} strokeWidth="6" />
                </g>
                <circle cx="50" cy="50" r="6" fill={color} />
            </svg>
            <div style={{ fontSize: '0.75em', color, fontWeight: 'bold' }}>{on ? 'RUNNING' : 'STOPPED'}</div>
        </div>
    );
};

const LabelBody = ({ widget }) => (
    <div style={{
        padding: '4px', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: (widget.fontSize || 18) + 'px',
        fontWeight: widget.bold ? 'bold' : 'normal',
        color: widget.color || '#222',
        textAlign: 'center'
    }}>
        {widget.label}
    </div>
);

// Connection line: renders inside the canvas-wide SVG layer.
// Resolves anchored endpoints from widget positions on every render.
const ConnectionLine = ({ line, widgets, devices, editMode, selected, onSelect }) => {
    const center = (id) => {
        const w = widgets.find(x => x.id === id);
        if (!w) return null;
        return { x: w.x + (w.w || 0) / 2, y: w.y + (w.h || 0) / 2 };
    };
    let p1, p2;
    if (line.mode === 'anchored') {
        p1 = center(line.anchorStart);
        p2 = center(line.anchorEnd);
        if (!p1 || !p2) return null;
    } else {
        p1 = { x: Number(line.x1) || 0, y: Number(line.y1) || 0 };
        p2 = { x: Number(line.x2) || 0, y: Number(line.y2) || 0 };
    }

    // Optional dynamic color from a device field
    let color = line.color || '#444';
    if (line.colorField && line.hwid) {
        const dev = devices.find(d => d.hwid === line.hwid);
        const v = getByPath(getDeviceLastData(dev), line.colorField);
        const on = isTruthyValue(v, line.onValue);
        color = on ? (line.onColor || '#28a745') : (line.offColor || '#999');
    }

    const dashed = line.dashed ? '6,4' : undefined;
    const sw = Number(line.strokeWidth) || 3;
    const arrowId = `arrow_${line.id}`;

    return (
        <g
           onMouseDown={(e) => { if (editMode) { e.stopPropagation(); onSelect(); } }}
           onClick={(e) => { if (editMode) { e.stopPropagation(); onSelect(); } }}>
            <defs>
                <marker id={arrowId} viewBox="0 0 10 10" refX="9" refY="5"
                    markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L10,5 L0,10 z" fill={color} />
                </marker>
            </defs>
            {/* Invisible thick hit-target so the line is easy to click in edit mode.
                pointer-events: stroke means the perimeter of the stroke is the click
                target regardless of color/opacity, which "transparent" alone wouldn't
                guarantee for visiblePainted. */}
            {editMode && (
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke="black" strokeOpacity="0"
                    strokeWidth={Math.max(sw + 12, 18)}
                    style={{ cursor: 'pointer', pointerEvents: 'stroke' }} />
            )}
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={selected ? '#007bff' : color}
                strokeWidth={selected ? sw + 1 : sw}
                strokeDasharray={dashed}
                markerEnd={(line.arrow === 'end' || line.arrow === 'both') ? `url(#${arrowId})` : undefined}
                markerStart={line.arrow === 'both' ? `url(#${arrowId})` : undefined}
                style={{ pointerEvents: editMode ? 'stroke' : 'none' }} />
        </g>
    );
};

// Per-threshold (min or max) input that lets the admin pick either a constant
// number or a field name from the device.  When "From field" is chosen, the
// live value of that field is used as the threshold at render time.
const ThresholdInput = ({ kind, widget, fields, onChange }) => {
    const isMin = kind === 'min';
    const valueKey = isMin ? 'alertMin' : 'alertMax';
    const fieldKey = isMin ? 'alertMinField' : 'alertMaxField';
    const currentField = widget[fieldKey] || '';
    const currentConstant = widget[valueKey] || '';
    // Local UI state — independent of whether a field has actually been picked yet,
    // so selecting "From field" doesn't snap back to Constant before you choose a field.
    const [mode, setMode] = useState(currentField ? 'field' : 'constant');
    // Reset the mode when the user clicks a different widget.
    useEffect(() => {
        setMode(widget[fieldKey] ? 'field' : 'constant');
    }, [widget.id, fieldKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const inputStyle = {
        width: '100%', padding: '5px',
        border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box'
    };
    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === 'constant') {
            // leaving field-mode: clear field reference, keep the constant
            if (currentField) onChange({ [fieldKey]: '' });
        } else {
            // entering field-mode: clear constant. The field is empty until user picks one.
            if (currentConstant) onChange({ [valueKey]: '' });
        }
    };
    return (
        <div style={{ display: 'flex', gap: '5px', marginBottom: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75em', color: '#666', minWidth: '24px' }}>
                {isMin ? 'Min' : 'Max'}
            </span>
            <select style={{ ...inputStyle, flex: '0 0 95px' }} value={mode}
                onChange={e => handleModeChange(e.target.value)}>
                <option value="constant">Constant</option>
                <option value="field">From field</option>
            </select>
            {mode === 'field' ? (
                <select style={inputStyle} value={currentField}
                    onChange={e => onChange({ [fieldKey]: e.target.value })}>
                    <option value="">-- select field --</option>
                    {fields.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
            ) : (
                <input style={inputStyle} type="number" step="any" value={currentConstant}
                    placeholder={isMin ? 'alert min' : 'alert max'}
                    onChange={e => onChange({ [valueKey]: e.target.value })} />
            )}
        </div>
    );
};

// ------------------------------------------------------------------ Config panel

const DeviceGroupSelector = ({ devices, deviceGroups, groupValue, hwid, groupKey, fieldKey, onChange, label = 'Hardware (HWID)' }) => {
    const selectedGroup = deviceGroups.find(group => group.name === groupValue);
    const selectableDevices = selectedGroup ? selectedGroup.devices : devices;
    const inputStyle = {
        width: '100%', padding: '5px', marginBottom: '6px',
        border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box'
    };
    const labelStyle = { fontSize: '0.8em', color: '#444', display: 'block', marginTop: '6px' };
    const selectGroup = (nextGroup) => {
        const group = deviceGroups.find(item => item.name === nextGroup);
        onChange({
            [groupKey]: nextGroup,
            hwid: group?.devices[0]?.hwid || '',
            [fieldKey]: ''
        });
    };
    const selectDevice = (nextHwid) => {
        const group = deviceGroups.find(item => item.devices.some(device => device.hwid === nextHwid));
        onChange({
            hwid: nextHwid,
            [groupKey]: group?.name || '',
            [fieldKey]: ''
        });
    };

    return (
        <>
            <label style={labelStyle}>Tag Group</label>
            <select style={inputStyle} value={groupValue || ''}
                onChange={e => selectGroup(e.target.value)}>
                <option value="">-- ทุกกลุ่ม --</option>
                {deviceGroups.map(group => (
                    <option key={group.name} value={group.name}>
                        {group.name === UNGROUPED_TAG_GROUP ? 'ไม่จัดกลุ่ม' : group.name} ({group.devices.length})
                    </option>
                ))}
            </select>
            <label style={labelStyle}>{label}</label>
            <select style={inputStyle} value={hwid}
                onChange={e => selectDevice(e.target.value)}>
                <option value="">-- select device --</option>
                {selectableDevices.map(device => (
                    <option key={device.hwid} value={device.hwid}>
                        {device.hwid} {(device.name || device.deviceName) ? `(${device.name || device.deviceName})` : ''}
                    </option>
                ))}
            </select>
        </>
    );
};

const WidgetConfig = ({ widget, devices, deviceGroups, widgets, onChange, onDelete }) => {
    const device = devices.find(d => d.hwid === widget.hwid);
    const fields = listLastDataFields(device);
    const inputStyle = {
        width: '100%', padding: '5px', marginBottom: '6px',
        border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box'
    };
    const labelStyle = { fontSize: '0.8em', color: '#444', display: 'block', marginTop: '6px' };
    const t = widget.type;
    const isLine = t === 'line';
    const isLabel = t === 'label';
    const showHardware = !isLine && !isLabel;
    const showField = !isLine && !isLabel;
    const showUnit = !isLine && !isLabel && t !== 'lamp' && t !== 'toggle';
    const showDisplayRange = t === 'gauge' || t === 'chart' || t === 'tank';
    const showThresholds = !isLine && !isLabel && t !== 'toggle';
    const showOnValue = t === 'lamp' || t === 'toggle' || t === 'pump';
    const showToggleCommands = t === 'toggle';
    const showLabelStyle = t === 'label';
    const showLineConfig = t === 'line';

    return (
        <div style={{ marginTop: '15px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
            <strong style={{ display: 'block', marginBottom: '8px', color: '#222' }}>
                Widget: {t}
            </strong>

            <label style={labelStyle}>Label</label>
            <input style={inputStyle} value={widget.label}
                onChange={e => onChange({ label: e.target.value })} />

            {showHardware && (
                <DeviceGroupSelector
                    devices={devices}
                    deviceGroups={deviceGroups}
                    groupValue={widget.tagGroup}
                    hwid={widget.hwid}
                    groupKey="tagGroup"
                    fieldKey="field"
                    onChange={onChange}
                />
            )}

            {showField && (
                <>
                    <label style={labelStyle}>Field (from lastDataJson)</label>
                    {fields.length > 0 ? (
                        <select style={inputStyle} value={widget.field}
                            onChange={e => onChange({ field: e.target.value })}>
                            <option value="">-- select field --</option>
                            {fields.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    ) : (
                        <input style={inputStyle} value={widget.field}
                            placeholder="e.g. temperature  (or path: foo.bar)"
                            onChange={e => onChange({ field: e.target.value })} />
                    )}
                </>
            )}

            {showUnit && (
                <>
                    <label style={labelStyle}>Unit</label>
                    <input style={inputStyle} value={widget.unit}
                        placeholder="C, %, kg ..."
                        onChange={e => onChange({ unit: e.target.value })} />
                </>
            )}

            {showDisplayRange && (
                <>
                    <label style={labelStyle}>Display range</label>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <input style={inputStyle} type="number" value={widget.rangeMin}
                            placeholder="min"
                            onChange={e => onChange({ rangeMin: e.target.value })} />
                        <input style={inputStyle} type="number" value={widget.rangeMax}
                            placeholder="max"
                            onChange={e => onChange({ rangeMax: e.target.value })} />
                    </div>
                </>
            )}

            {(t === 'numeric' || t === 'gauge' || t === 'tank' || t === 'chart') && (
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="checkbox" checked={!!widget.invert}
                        onChange={e => onChange({ invert: e.target.checked })} />
                    Invert value (display = min + max - raw)
                    <span style={{ fontSize: '0.85em', color: '#888' }}>
                        e.g. sonar distance to water -> water level
                    </span>
                </label>
            )}

            {showOnValue && (
                <>
                    <label style={labelStyle}>"ON" value (string match against the field)</label>
                    <input style={inputStyle} value={widget.onValue || ''}
                        placeholder='e.g. "1", "true", "ON" — leave blank for truthy'
                        onChange={e => onChange({ onValue: e.target.value })} />
                </>
            )}

            {showToggleCommands && (
                <>
                    <label style={labelStyle}>ON command (JSON or text)</label>
                    <textarea style={{ ...inputStyle, fontFamily: 'monospace' }} rows={2}
                        value={widget.onCommand || ''}
                        onChange={e => onChange({ onCommand: e.target.value })} />
                    <label style={labelStyle}>OFF command (JSON or text)</label>
                    <textarea style={{ ...inputStyle, fontFamily: 'monospace' }} rows={2}
                        value={widget.offCommand || ''}
                        onChange={e => onChange({ offCommand: e.target.value })} />
                    <label style={labelStyle}>Command reference (optional)</label>
                    <input style={inputStyle} value={widget.commandRef || ''}
                        onChange={e => onChange({ commandRef: e.target.value })} />
                </>
            )}

            {showLabelStyle && (
                <>
                    <label style={labelStyle}>Font size (px)</label>
                    <input style={inputStyle} type="number" value={widget.fontSize || 18}
                        onChange={e => onChange({ fontSize: Number(e.target.value) || 18 })} />
                    <label style={labelStyle}>Color</label>
                    <input style={inputStyle} value={widget.color || '#222'}
                        onChange={e => onChange({ color: e.target.value })} />
                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input type="checkbox" checked={!!widget.bold}
                            onChange={e => onChange({ bold: e.target.checked })} />
                        Bold
                    </label>
                </>
            )}

            {showLineConfig && (
                <>
                    <label style={labelStyle}>Endpoint mode</label>
                    <select style={inputStyle} value={widget.mode || 'free'}
                        onChange={e => onChange({ mode: e.target.value })}>
                        <option value="free">Free coordinates</option>
                        <option value="anchored">Anchored to widgets</option>
                    </select>

                    {widget.mode === 'anchored' ? (
                        <>
                            <label style={labelStyle}>Start widget</label>
                            <select style={inputStyle} value={widget.anchorStart || ''}
                                onChange={e => onChange({ anchorStart: e.target.value })}>
                                <option value="">-- select --</option>
                                {(widgets || []).filter(w => w.type !== 'line' && w.id !== widget.id).map(w => (
                                    <option key={w.id} value={w.id}>{w.label || w.type} ({w.type})</option>
                                ))}
                            </select>
                            <label style={labelStyle}>End widget</label>
                            <select style={inputStyle} value={widget.anchorEnd || ''}
                                onChange={e => onChange({ anchorEnd: e.target.value })}>
                                <option value="">-- select --</option>
                                {(widgets || []).filter(w => w.type !== 'line' && w.id !== widget.id).map(w => (
                                    <option key={w.id} value={w.id}>{w.label || w.type} ({w.type})</option>
                                ))}
                            </select>
                        </>
                    ) : (
                        <>
                            <label style={labelStyle}>Start (X1, Y1)</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input style={inputStyle} type="number" value={widget.x1 ?? 0}
                                    onChange={e => onChange({ x1: Number(e.target.value) })} />
                                <input style={inputStyle} type="number" value={widget.y1 ?? 0}
                                    onChange={e => onChange({ y1: Number(e.target.value) })} />
                            </div>
                            <label style={labelStyle}>End (X2, Y2)</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input style={inputStyle} type="number" value={widget.x2 ?? 0}
                                    onChange={e => onChange({ x2: Number(e.target.value) })} />
                                <input style={inputStyle} type="number" value={widget.y2 ?? 0}
                                    onChange={e => onChange({ y2: Number(e.target.value) })} />
                            </div>
                        </>
                    )}

                    <label style={labelStyle}>Stroke color</label>
                    <input style={inputStyle} value={widget.color || '#444'}
                        onChange={e => onChange({ color: e.target.value })} />

                    <label style={labelStyle}>Stroke width</label>
                    <input style={inputStyle} type="number" value={widget.strokeWidth || 3}
                        onChange={e => onChange({ strokeWidth: Number(e.target.value) || 3 })} />

                    <label style={labelStyle}>Arrow</label>
                    <select style={inputStyle} value={widget.arrow || 'none'}
                        onChange={e => onChange({ arrow: e.target.value })}>
                        <option value="none">None</option>
                        <option value="end">End only</option>
                        <option value="both">Both ends</option>
                    </select>

                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input type="checkbox" checked={!!widget.dashed}
                            onChange={e => onChange({ dashed: e.target.checked })} />
                        Dashed
                    </label>

                    <DeviceGroupSelector
                        devices={devices}
                        deviceGroups={deviceGroups}
                        groupValue={widget.lineTagGroup}
                        hwid={widget.hwid || ''}
                        groupKey="lineTagGroup"
                        fieldKey="colorField"
                        label="Dynamic-color device (optional)"
                        onChange={onChange}
                    />
                    {widget.hwid && (
                        <>
                            <input style={inputStyle} value={widget.colorField || ''}
                                placeholder="field name (truthy = on color)"
                                onChange={e => onChange({ colorField: e.target.value })} />
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input style={inputStyle} value={widget.onColor || '#28a745'}
                                    placeholder="on color"
                                    onChange={e => onChange({ onColor: e.target.value })} />
                                <input style={inputStyle} value={widget.offColor || '#999'}
                                    placeholder="off color"
                                    onChange={e => onChange({ offColor: e.target.value })} />
                            </div>
                        </>
                    )}
                </>
            )}

            {showThresholds && (
                <>
                    <label style={labelStyle}>{'Alert thresholds (alarm if value < min or > max)'}</label>
                    <ThresholdInput kind="min" widget={widget} fields={fields} onChange={onChange} />
                    <ThresholdInput kind="max" widget={widget} fields={fields} onChange={onChange} />
                    {/* Live preview */}
                    {(() => {
                        const liveData = getDeviceLastData(device);
                        const rawLive = getByPath(liveData, widget.field);
                        const liveValue = applyInvert(rawLive, widget); // display only
                        // Alarm comparison uses raw value, not inverted display.
                        const ev = resolveAndEvaluate(rawLive, widget, liveData);
                        const r = resolveThresholds(widget, liveData);
                        const lo = r.lo;
                        const hi = r.hi;
                        return (
                            <div style={{
                                marginTop: '6px', padding: '6px',
                                backgroundColor: ev.alarm ? '#fff5f5' : '#f4f9f4',
                                border: '1px solid ' + (ev.alarm ? '#dc3545' : '#28a745'),
                                borderRadius: '3px', fontSize: '0.78em'
                            }}>
                                <div>
                                    Live value: <strong>{liveValue == null ? '-' : String(liveValue)}</strong>
                                    {widget.invert && rawLive != null && (
                                        <span style={{ color: '#888', marginLeft: '6px', fontSize: '0.9em' }}>
                                            (raw: {String(rawLive)} → inverted)
                                        </span>
                                    )}
                                </div>
                                <div>
                                    Min: <strong>{lo == null ? '-' : lo}</strong>
                                    {'  '}Max: <strong>{hi == null ? '-' : hi}</strong>
                                    {widget.invert && (
                                        <span style={{ color: '#888', marginLeft: '6px' }}>
                                            (alarm compares against raw {rawLive == null ? '-' : String(rawLive)})
                                        </span>
                                    )}
                                </div>
                                <div style={{ color: ev.alarm ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
                                    {ev.alarm ? 'ALARM (' + ev.why + ')' : 'within range'}
                                </div>
                            </div>
                        );
                    })()}
                </>
            )}

            <button onClick={onDelete}
                style={{
                    width: '100%', marginTop: '10px', padding: '6px',
                    backgroundColor: '#dc3545', color: 'white', border: 'none',
                    borderRadius: '3px', cursor: 'pointer'
                }}>
                Delete widget
            </button>
        </div>
    );
};

// inject one-time pulse keyframes
if (typeof document !== 'undefined' && !document.getElementById('scada-pulse-style')) {
    const s = document.createElement('style');
    s.id = 'scada-pulse-style';
    s.innerHTML = '@keyframes scada-pulse {' +
        '0% { box-shadow: 0 0 4px rgba(220,53,69,0.4); }' +
        '50% { box-shadow: 0 0 16px rgba(220,53,69,0.9); }' +
        '100% { box-shadow: 0 0 4px rgba(220,53,69,0.4); }' +
    '}' +
    '@keyframes scada-spin {' +
        'from { transform: rotate(0deg); }' +
        'to { transform: rotate(360deg); }' +
    '}';
    document.head.appendChild(s);
}

export default ScadaPage;
